import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { submitTeacherSignup, checkValidEmailDomain } from '@/lib/muzology';
import { SmallButton } from '@/components/Views/Buttons';
import Button from '@/components/ReusableUI/Button';
import { MUZOLOGY_API } from '@/lib/defaults';
import GoogleLogo from '@/assets/logos/google-g.svg';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import { useRouter } from 'next/router';
import { BlockKlaviyo, useAnalytics } from '@/analytics/AnalyticsProvider';
import { NewTeacherAccount, NewTeacherTrial, TeacherSignupSuccess } from '@/analytics/events';
import * as Events from '@/analytics/events';
import { getResponseRedirection } from '@/lib/redirect';
import { createUrlWithParams } from '@/lib/urls';

interface FormFields {
    name: string;
    email: string;
    password: string;
}

const FormTeacherTrialSignup = ({ className = '', googleButton = true }) => {
    const analytics = useAnalytics();
    const [loading, setLoading] = useState(false);
    const router = useRouter();
    const { email, token } = router.query ?? {};
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    const togglePasswordVisibility = (event) => {
        event.preventDefault();
        setIsPasswordVisible(!isPasswordVisible);
    };

    const defaultValues = {
        email,
        name: '',
        password: '',
        terms: false,
        age: false
    };

    const {
        register,
        handleSubmit,
        watch,
        reset,
        setError,
        formState: { errors }
    } = useForm({ defaultValues });

    // reset the form when the email changes
    useEffect(() => reset({ email }), [email, reset]);

    const onSubmit = async (data) => {
        setLoading(true);
        const anon = analytics.getAnonymousId();
        // eslint-disable-next-line camelcase
        const signup = { ...data, anonymous_id: anon };
        const event = { ...signup };
        delete event.password;
        await analytics.track('Teacher Signup Clicked Submit', event, BlockKlaviyo);
        submitTeacherSignup(signup, false)
            .then(async (response) => {
                const { data } = response;
                console.log('Teacher Signup Success:', data);
                await analytics.track(Events.TeacherSignupSuccess, data, BlockKlaviyo);
                await analytics.track(Events.NewTeacherAccount, data);
                await analytics.track(Events.NewTeacherTrial, data);
                console.log(data);
                const redirect = getResponseRedirection(response);
                if (redirect) {
                    window.location.href = redirect;
                }
            })
            .catch(async (err) => {
                setLoading(false);
                if (err.response?.status === 400) {
                    if (typeof err.response?.data === 'object') {
                        console.log(err.response.data);
                        // set form errors
                        for (const [key, value] of Object.entries(err.response.data)) {
                            console.log(key);
                            console.log(value.toString());
                            setError(key as keyof FormFields, { type: 'manual', message: value.toString() });
                        }
                        await analytics.track('Teacher Signup Failed', err.response.data);
                    } else {
                        await analytics.track('Teacher Signup Error', err.response);
                    }
                } else {
                    await analytics.track('Teacher Signup Error', err.response);
                    console.error(err);
                }
            });
    };

    const terms = (
        <a className='text-black' href='/tos' target='_blank'>
            Terms of Use
        </a>
    );
    const privacy = (
        <a className='text-black' href='/privacy' target='_blank'>
            Privacy Policy
        </a>
    );

    // user clicked the Google signup button
    const handleGoogleSignup = async () => {
        // force them to agree to the terms
        // if (!watch('terms')) {
        //     reset();
        //     setError('terms', {
        //         type: 'manual',
        //         message: 'You must agree to the terms and conditions'
        //     });
        //     return;
        // }

        // track the event
        const anon = analytics.getAnonymousId();
        const signup = { ...router.query, terms: watch('terms'), anonymousId: anon };
        const url = createUrlWithParams(`${MUZOLOGY_API}/google_signup/`, signup);
        setLoading(true);
        console.log('Teacher Signup Clicked Google', signup);
        await analytics.track('Teacher Signup Clicked Google', signup);
        window.location.href = url;
    };

    return (
        <>
            <LoadingOverlay show={loading} className='fixed z-50' />
            <div className={className}>
                <div className='relative flex flex-col justify-center overflow-hidden px-4'>
                    <div className='w-full max-w-[410px] mt- mx-auto mb-8 py-8 sm:py-4 px-7 m-auto bg-white rounded-2xl shadow-md drop-shadow-lg '>
                        {googleButton && (
                            <>
                                <div className='mx-auto w-full max-w-[100%] text-center'>
                                    <Button
                                        className='google-signup-button hover:bg-[#F9FAFB] active:bg-[#E9EAEB] m-[auto] px-1 py-4 rounded-md bg-[#F2F2F2] border-[1.5px] border-solid'
                                        // border='#D1D5DB'
                                        onClick={handleGoogleSignup}
                                        // onClick={() => {
                                        //     setLoading(true);
                                        //     window.location.href = `${MUZOLOGY_API}/google_signup/`;
                                        // }}
                                    >
                                        <div className='flex gap-2 justify-center px-4 items-center'>
                                            <GoogleLogo />
                                            <span className='text-[#1F1F1F] font-bold text-base'>Sign Up With Google</span>
                                        </div>
                                    </Button>
                                </div>

                                {/* divider line */}
                                <div
                                    className='h-[2.2px] max-w-[1216px] mx-auto my-4'
                                    style={{
                                        background:
                                            'linear-gradient(90deg, rgba(110, 96, 230, 0.00) 0%, rgba(110, 96, 230, 0.30) 50.16%, rgba(110, 96, 230, 0.00) 100%)'
                                    }}
                                />

                                <div className=' pt-0 pb-5'>
                                    <div className='text-[#6B7280] mx-auto w-[70%] h-[1px] text-base text-center'>Sign Up With Email</div>
                                </div>
                            </>
                        )}
                        {/* <p className='text-center text-[#6B7280] text-sm mt-0 '>Sign up with email</p> */}
                        <form onSubmit={handleSubmit(onSubmit)} className='mt-4 flex flex-col gap-5'>
                            {/*
                            <div className=''>
                                <label className='block text-sm font-semibold text-gray-800'>Full Name</label>
                                <input
                                    type='input'
                                    autoComplete='name'
                                    {...register('name', { required: true })}
                                    className='block w-full px-4 py-2 mt-2 text-[#6B7280] bg-white border-[#D1D5DB] border-[1.5px] border-solid rounded-md focus:border-[#5EC5DF] focus:ring-[#5EC5DF] focus:outline-none focus:ring focus:ring-opacity-40'
                                />
                                {errors.name && <div className='mt-1 text-red-500'>* This field is required</div>}
                            </div>
                            */}
                            <div className=''>
                                <label className='block text-sm font-semibold text-gray-800'>School Email</label>
                                <input
                                    type='email'
                                    // autoComplete='email'
                                    {...register('email', {
                                        required: true,
                                        validate: checkValidEmailDomain
                                    })}
                                    className='block w-full px-4 py-2 mt-2 text-[#6B7280] bg-white border-[#D1D5DB] border-[1.5px] border-solid rounded-md focus:border-[#5EC5DF] focus:ring-[#5EC5DF] focus:outline-none focus:ring focus:ring-opacity-40'
                                />
                                {errors.email && errors.email.type === 'required' && <div className='mt-1 text-red-500'>* This field is required</div>}
                                {errors.email && errors.email.type === 'validate' && (
                                    <div className='mt-1 text-red-500'>* Please use your official work/school email address</div>
                                )}
                                {errors.email && errors.email.type === 'manual' && (
                                    <div className='mt-1 text-red-500'>
                                        * <span dangerouslySetInnerHTML={{ __html: errors.email.message as string }} />
                                    </div>
                                )}
                            </div>
                            <div className='relative'>
                                <label className='block text-sm font-semibold text-gray-800'>Create Password</label>
                                <input
                                    type={isPasswordVisible ? 'text' : 'password'}
                                    autoComplete='new-password'
                                    {...register('password', { required: true })}
                                    className='block w-full px-4 py-2 mt-2 text-[#6B7280] bg-white border-[#D1D5DB] border-[1.5px] border-solid rounded-md focus:border-[#5EC5DF] focus:ring-[#5EC5DF] focus:outline-none focus:ring focus:ring-opacity-40'
                                />
                                {errors.password && <div className='mt-1 text-red-500'>* This field is required</div>}
                                <button
                                    className='absolute p-2 border-none right-0 top-7 bottom-0 w-[36.5px] text-[#6B7280] hover:text-MuzOrange active:text-MuzPink bg-[transparent] '
                                    onClick={togglePasswordVisibility}
                                >
                                    <svg className='h-full' width='100%' height='100%' viewBox='0 0 24 24' fill='red' xmlns='http://www.w3.org/2000/svg'>
                                        <path
                                            fillRule='evenodd'
                                            clipRule='evenodd'
                                            d='M12 4C16.2909 4 19.8627 6.4287 22.6646 11.154L22.8844 11.5329L22.9302 11.6328L22.9591 11.716L22.9736 11.7706L22.988 11.853L22.999 11.9535L22.9982 12.0639L22.9849 12.1744C22.9784 12.2112 22.9698 12.2477 22.9591 12.284L22.9203 12.3916L22.8844 12.4671L22.8682 12.4962C20.1039 17.3334 16.5685 19.8773 12.3127 19.9957L12 20C7.6044 20 3.96348 17.4514 1.13177 12.4962C0.956077 12.1887 0.956077 11.8113 1.13177 11.5038C3.96348 6.54862 7.6044 4 12 4ZM12 6C8.59033 6 5.69918 7.89478 3.27859 11.8059L3.161 12L3.27859 12.1941C5.63002 15.9935 8.42551 17.8901 11.709 17.9954L12 18C15.4097 18 18.3008 16.1052 20.7214 12.1941L20.838 12L20.7214 11.8059C18.37 8.00652 15.5745 6.10992 12.291 6.00464L12 6ZM12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9ZM12 11C12.5523 11 13 11.4477 13 12C13 12.5523 12.5523 13 12 13C11.4477 13 11 12.5523 11 12C11 11.4477 11.4477 11 12 11Z'
                                            fill='currentColor'
                                        />
                                    </svg>
                                </button>
                            </div>

                            <div className='mt-0'>
                                <SmallButton className='blue-button w-full tracking-wide transition-colors duration-200 transform rounded-md'>
                                    Sign Up
                                </SmallButton>
                            </div>

                            <div className='mb-3 text-base font-light text-[#303030]'>
                                By clicking Sign Up, you certify you're 18+ and agree to the Muzology{' '}
                                <a className='text-black underline' href='https://muzology.com/tos' target='_blank' rel='noopener noreferrer'>
                                    Terms of Service
                                </a>{' '}
                                and{' '}
                                <a className='text-black underline' href='https://muzology.com/privacy' target='_blank' rel='noopener noreferrer'>
                                    Privacy Policy
                                </a>
                                .
                            </div>
                        </form>
                        {/*
                        <div className='mt-3 mx-auto w-full max-w-[100%] text-center'>
                            <Button
                                className='google-signup-button hover:bg-[#F9FAFB] active:bg-[#E9EAEB] m-[auto] px-1 py-4 rounded-md bg-white border-[#D1D5DB] border-[1.5px] border-solid'
                                border='#D1D5DB'
                                onClick={() => {
                                    setLoading(true);
                                    window.location.href = `${MUZOLOGY_API}/facebook_signup/`;
                                }}
                            >
                                <div className='flex gap-2 justify-center px-4 items-center'>
                                    <div>
                                        <svg
                                            width='15' height='16' viewBox='0 0 15 16' fill='none'
                                            xmlns='http://www.w3.org/2000/svg'>
                                            <path
                                                fillRule='evenodd'
                                                clipRule='evenodd'
                                                d='M7.77517 0.902832C11.716 0.902832 14.9107 4.09751 14.9107 8.03836C14.9107 11.5999 12.3014 14.5518 8.89022 15.0872V10.101H10.5528L10.8692 8.03838H10.8691L10.8691 8.03836H8.89022V6.69987C8.89022 6.13558 9.16665 5.58555 10.0531 5.58555H10.9528V3.82954C10.9528 3.82954 10.9528 3.82953 10.9527 3.82951C10.9527 3.82951 10.1361 3.69015 9.35544 3.69015C7.72553 3.69015 6.66025 4.67797 6.66025 6.46631V8.03836H4.84849V10.101H4.84861V10.101H6.66025V15.0872C3.24901 14.5519 0.639648 11.5999 0.639648 8.03836C0.639648 4.09751 3.83433 0.902832 7.77517 0.902832Z'
                                                fill='#1877F2'
                                            />
                                        </svg>
                                    </div>
                                    <span className='text-[#333333] font-bold text-base'>Sign Up With Facebook</span>
                                </div>
                            </Button>
                        </div>
                        */}
                    </div>
                </div>
            </div>
        </>
    );
};

export default FormTeacherTrialSignup;
